import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Col, Row } from 'antd';
import dayjs from 'dayjs';
import { Container, FormFieldHeading } from '@/components/Elements';
import { YourRefinanceDetailFormType } from '../types';
import { useCountryOptions } from '@/hooks/useCountryOptions';
import {
  DatePickerField,
  FormLabel,
  InputField,
  RadioGroupField,
  SelectField,
} from '@/components/Form';

import useUnsavedChangesWarning from '@/hooks/useUnsavedChangesWarning';
import { personalDetailLabelMapper } from '../schema/personalDetailSchema';
import {
  AGREE_OPTIONS,
  CITIZENSHIP_OPTIONS,
  DEPENDENT_CHILDREN_OPTIONS,
  FREQUENCY_OPTIONS,
  MARITIAL_STATUS_OPTIONS,
  PRIVATE_SCHOOL_OPTIONS,
  RESIDENTIAL_STATUS_OPTIONS,
} from '@/constant/options';
import { refinanceFormRoutes } from '../constant/refinanceStepperRoutes';
import useScrollToTop from '@/hooks/useScrollTop';
import { useFetchUserProfileQuery } from '@/store/apis/userApi';
import { displayError, displaySuccess } from '@/utils/displayMessageUtils';
import { REFINANCE_PRIORITY_OPTIONS } from '../constant/optionList';
import { usePostRefinanceRequestMutation } from '@/pages/mortgage/apis/refinanceAPI';
import StepProgress from '@/components/StepProgress/StepProgress';
import { stepData } from '../constant/stepData';
import FormMainLabel from '@/components/Elements/Heading/FormMainLabel';

interface Props {
  data: any;
  setValue: any;
  watch: any;
  control: any;
  errors: any;
  unregister: any;
  getValues: any;
  resetField: any;
}

const NewPersonalDetail: React.FC<Props> = ({
  data: personalDetailData,
  setValue,
  watch,
  control,
  errors,
  unregister,
  getValues,
  resetField,
}) => {
  const { id } = useParams();
  useUnsavedChangesWarning(true);
  useScrollToTop();
  const countryOptions = useCountryOptions();
  const navigate = useNavigate();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const total = 5;
  const [postRefinanceRequest, { isLoading: postRefinanceRequestLoading }] =
    usePostRefinanceRequestMutation();

  const {
    data: userProfileData,
    // error: userProfileDataError,
    // isLoading: userProfileDataLoading,
  } = useFetchUserProfileQuery(undefined, {
    skip: !!id,
  });

  useEffect(() => {
    if (id && personalDetailData) {
      setValue('refinancePriority', personalDetailData?.refinancePriority);
      setValue('citizenship', personalDetailData?.citizenship);
      if (
        personalDetailData?.citizenship === 'australian_pr_visa' ||
        personalDetailData?.citizenship === 'australian_tr_visa'
      ) {
        setValue('visaSubClass', personalDetailData?.visaSubClass);
        setValue('visaExpiryDate', personalDetailData?.visaExpiryDate);
      }
      setValue(
        'currentResidentalStatus',
        personalDetailData?.currentResidentalStatus,
      );
      setValue('country', personalDetailData?.country);
      setValue('permanentResident', personalDetailData?.permanentResident);
      setValue('kidsAge', personalDetailData?.kidsAge);
      setValue('privateSchool', personalDetailData?.privateSchool);
      setValue(
        'currentResidentalStatus',
        personalDetailData?.currentResidentalStatus,
      );
      setValue('maritalStatus', personalDetailData?.maritalStatus);
      setValue('dependentChildren', personalDetailData?.dependentChildren);
      setValue('numberOfDependents', personalDetailData?.numberOfDependents);
      if (personalDetailData?.currentResidentalStatus === 'renting') {
        setValue('rentExpense', personalDetailData?.rentExpense);
        setValue('frequencyOfRent', personalDetailData?.frequencyOfRent);
      }
      if (personalDetailData?.currentResidentalStatus === 'live_own_place') {
        setValue(
          'isTheResidentialPlaceMortgaged',
          personalDetailData?.isTheResidentialPlaceMortgaged,
        );
      }
    } else if (!id && userProfileData) {
      const userDetail = userProfileData.data.common_data;
      setValue('citizenship', userDetail?.citizenship ?? null);
      setValue('country', userDetail?.country ?? null);
      setValue(
        'currentResidentalStatus',
        userDetail?.currentResidentalStatus ?? null,
      );
      setValue('maritalStatus', userDetail?.maritalStatus ?? null);
      setValue('refinancePriority', userDetail?.refinancePriority ?? []);
    }
  }, [id, setValue, userProfileData, personalDetailData]);

  const watchCitizenship = watch('citizenship');
  const watchCurrentResidentalStatus = watch('currentResidentalStatus');
  const watchMaritalStatus = watch('maritalStatus');
  const watchDependentChildren = watch('dependentChildren');
  const watchCountry = watch('country');
  const onSubmit = async (data: YourRefinanceDetailFormType) => {
    try {
      const res = await postRefinanceRequest({
        data,
        step: 'personal_detail',
        application_id: id,
      });

      // Check if the application ID is available
      const applicationId = res?.data?.data?.application_id;

      if (applicationId) {
        displaySuccess('Successfully submitted details.');
        navigate(`${refinanceFormRoutes['property_detail']}/${applicationId}`);
      } else {
        throw new Error('Application ID not found in response');
      }
    } catch (err) {
      displayError(err);
    }
  };

  const fieldOrder = [
    'citizenship',
    'citizenshipDetails',
    'currentResidentalStatus',
    'currentResidentalStatusDetails',
    'maritalStatus',
    'maritalStatusDetails',
  ];

  const numberOfDependents = watch('numberOfDependents');
  const kidsAge = watch('kidsAge') || [];

  useEffect(() => {
    const targetLength = numberOfDependents || 0;

    let currentKidsAge = [];
    if (personalDetailData?.numberOfDependents) {
      currentKidsAge = personalDetailData.kidsAge || [];
    } else {
      currentKidsAge = getValues('kidsAge') || [];
    }

    const updated = currentKidsAge.slice(0, targetLength);
    while (updated.length < targetLength) {
      updated.push(null);
    }
    setValue('kidsAge', updated);
  }, [numberOfDependents, setValue, getValues, personalDetailData]);

  useEffect(() => {
    if (watchCountry) {
      resetField('permanentResident');
    }
  }, [watchCountry, resetField]);

  return (
    <Container width="md">
      <Row>
        <Col xs={24}>
          <Row gutter={30}>
            <Col xs={24} md={24}>
              <div className="form-control form-section-header">
                <FormMainLabel
                  index={1}
                  label={personalDetailLabelMapper['refinancePriority']}
                />
                <RadioGroupField
                  size="large"
                  control={control}
                  name="refinancePriority"
                  options={REFINANCE_PRIORITY_OPTIONS}
                  error={errors.refinancePriority?.message}
                  multiple={true}
                />
              </div>
            </Col>
            <Col xs={24}>
              <div
                className="form-control form-section-header"
                id="citizenship"
              >
                <FormMainLabel
                  index={2}
                  label={personalDetailLabelMapper['citizenship']}
                />
                <RadioGroupField
                  size="large"
                  control={control}
                  name="citizenship"
                  options={CITIZENSHIP_OPTIONS}
                  error={errors.citizenship?.message}
                />
              </div>
            </Col>
          </Row>
          <Row gutter={30} id="citizenshipDetails">
            {(watchCitizenship === 'australian_pr_visa' ||
              watchCitizenship === 'australian_tr_visa') && (
              <>
                <Col xs={24} md={12}>
                  <InputField
                    label="Visa Sub-Class"
                    name="visaSubClass"
                    control={control}
                    error={errors.visaSubClass?.message ?? ''}
                    placeholder="Enter your visa subclass"
                    size="large"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <DatePickerField
                    label="Visa Expiry Date"
                    name="visaExpiryDate"
                    control={control}
                    error={errors.visaExpiryDate?.message ?? ''}
                    placeholder="Select Visa Expiry Date"
                    minDate={dayjs()}
                    size="large"
                  />
                </Col>
              </>
            )}
          </Row>
          <Row gutter={30}>
            <Col xs={24}>
              <div className="form-control form-section-header">
                <FormMainLabel
                  index={3}
                  label={personalDetailLabelMapper['country']}
                />
                <SelectField
                  showSearch={true}
                  options={countryOptions}
                  name="country"
                  control={control}
                  placeholder="Select your country"
                  error={errors.country?.message ?? ''}
                  size="large"
                />
              </div>
            </Col>
            {watchCountry === 'SG' && (
              <Col xs={24}>
                <FormLabel
                  label="Are you a permanent resident of this country?"
                  size="large"
                />
                <RadioGroupField
                  name="permanentResident"
                  control={control}
                  error={errors.permanentResident?.message}
                  options={AGREE_OPTIONS}
                  cols={{ sm: 1, md: 1, lg: 2 }}
                  size="large"
                />
              </Col>
            )}
            <Col xs={24}>
              <div
                className="form-control form-section-header"
                id="currentResidentalStatus"
              >
                <FormMainLabel
                  index={4}
                  label={personalDetailLabelMapper['currentResidentalStatus']}
                />
                <RadioGroupField
                  size="large"
                  control={control}
                  name="currentResidentalStatus"
                  options={RESIDENTIAL_STATUS_OPTIONS}
                  error={errors.currentResidentalStatus?.message}
                />
              </div>
            </Col>
          </Row>
          <Row gutter={30} id="currentResidentalStatusDetails">
            {watchCurrentResidentalStatus === 'renting' && (
              <>
                <Col xs={24} md={12}>
                  <InputField
                    label="Rent Expense (In the currency of your country of residence)"
                    type="number"
                    name="rentExpense"
                    control={control}
                    error={errors.rentExpense?.message ?? ''}
                    placeholder="Enter the rent expense of your country of residence"
                    size="large"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <SelectField
                    label="Frequency of Rent"
                    options={FREQUENCY_OPTIONS}
                    name="frequencyOfRent"
                    filterName="country"
                    control={control}
                    size="large"
                    placeholder="Frequency of Rent"
                    error={errors.frequencyOfRent?.message ?? ''}
                  />
                </Col>
              </>
            )}

            {watchCurrentResidentalStatus === 'live_own_place' && (
              <Col xs={24}>
                <SelectField
                  options={AGREE_OPTIONS}
                  label="Is This Place Mortgaged?"
                  name="isTheResidentialPlaceMortgaged"
                  control={control}
                  error={errors.isTheResidentialPlaceMortgaged?.message ?? ''}
                  placeholder="Is this place mortgaged?"
                  size="large"
                />
              </Col>
            )}
            {watchCurrentResidentalStatus === 'other' && (
              <Col xs={24} md={12}>
                <InputField
                  label="Provide Further Specification"
                  name="residentalOtherSpecification"
                  control={control}
                  error={errors.residentalOtherSpecification?.message ?? ''}
                  placeholder="Please provide further specification of your current residental status"
                  size="large"
                />
              </Col>
            )}
          </Row>
          <Row gutter={30} id="maritalStatus">
            <Col xs={24}>
              <div className="form-control form-section-header">
                <FormMainLabel
                  index={5}
                  label={personalDetailLabelMapper['maritalStatus']}
                />
                <RadioGroupField
                  size="large"
                  control={control}
                  name="maritalStatus"
                  options={MARITIAL_STATUS_OPTIONS}
                  error={errors.maritalStatus?.message}
                />
              </div>
            </Col>
          </Row>
          <Row gutter={[30, 30]} id="maritalDetails">
            <Col xs={24}>
              <div className="form-section-header form-control">
                <FormMainLabel
                  index={6}
                  label={personalDetailLabelMapper['dependentChildren']}
                />
                <RadioGroupField
                  name="numberOfDependents"
                  control={control}
                  error={errors.numberOfDependents?.message}
                  options={DEPENDENT_CHILDREN_OPTIONS}
                  cols={{ sm: 3, md: 4, lg: 6 }}
                  size="large"
                />
              </div>
            </Col>

            {(kidsAge || []).map((_: any, index: number) => (
              <Col key={index} xs={24} md={6} lg={4}>
                <InputField
                  label={`Kids Age ${index + 1}`}
                  name={`kidsAge.${index}`}
                  type="number"
                  control={control}
                  error={errors?.kidsAge?.[index]?.message ?? ''}
                  size="large"
                />
              </Col>
            ))}
            {numberOfDependents > 0 && (
              <Col xs={24}>
                <SelectField
                  options={PRIVATE_SCHOOL_OPTIONS}
                  label="Do your children go to private school?"
                  placeholder="Do your children go to private school?"
                  name="privateSchool"
                  control={control}
                  error={errors.residentalOtherSpecification?.message ?? ''}
                  size="large"
                />
              </Col>
            )}
          </Row>
          <Col xs={24}>
            <Button
              ref={submitButtonRef}
              type="primary"
              htmlType="submit"
              style={{ display: 'none' }}
            >
              Submit
            </Button>
          </Col>
        </Col>
      </Row>
    </Container>
  );
};

export default NewPersonalDetail;
